import {RREventsType, RREventType} from "../../types/RREventType";
import {RREmployeesType, RREmployeeType} from "../../types/RREmployeeType";
import {dateToString, dateWithZeroTime, getDateAsPeriod, timeDiffInMinutes} from "../../tools";
import {collection, getDocs, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {calendarDataConverter,} from "../../../firebase/FirebaseCalendarLoader";
import {getEmployeeRateEffectiveFrom} from "../employees/employeeRateAudit";

export type RRReportType = {
    periodFrom: Date;
    periodTo: Date;
    report: {
        events: RREventReportReportItemsType;
        employees: RREmployeeReportItemsType;
    }
}

export type RRReportItemType = {
    date: string;
    minutesAsSupervisor: number;
    minutesAsHoliday: number;
    minutes: number;
    price: number;
    description?: string;
}

export type RRReportItemsType = RRReportItemType[];



export type RREventReportReportItemType = RRReportItemType & {
    event: RREventType;
    employees: RREmployeeReportItemType[];
}

export type RREventReportReportItemsType = RREventReportReportItemType[];


export type RREmployeeReportItemType = RRReportItemType & {
    employee: RREmployeeType;
}

export type RREmployeeReportItemsType = RREmployeeReportItemType[];

export default async function calculateReport(periodFrom: Date, periodTo: Date,
                                              allEvents: RREventsType,
                                              employees: RREmployeesType,
                                              holidayDates: string[],
                                              department: string,
                                              onlyForEmployeeId?: string) {
    const [periodToStart, periodToEnd] = getDateAsPeriod(periodTo);

    const report: RRReportType = {
        periodFrom: dateWithZeroTime(periodFrom)!,
        periodTo: dateWithZeroTime(periodTo)!,
        report: {
            events: [],
            employees: [],
        }
    };

    const calendarConstraints = [where("eventDayStart", ">=", report.periodFrom),
        where("eventDayStart", "<", periodToEnd),
        where("dayType", "in", ["WORKDAY", "HOLIDAY"]),
        where("department", "==", department),
        orderBy("eventDayStart", "asc")];
    if (onlyForEmployeeId) {
        calendarConstraints.push(where("employeeId", "==", onlyForEmployeeId));
    }

    const calendar = await getDocs(query(collection(appFirestore, "calendar"), ...calendarConstraints));
    const calendarItems = calendarDataConverter(calendar.docs);

    const createWarehouseEvent = (): RREventType => ({
        eventNumber: "WAREHOUSE",
        eventDayStart: undefined,
        eventDayEnd: undefined,
        id: "WAREHOUSE",
        clientName: department === "store" ? "КУХНЯ" : "СКЛАД",
        clientEmail: "",
        eventCategory: "MONTAGE",
        isCatering: false,
        eventName: department === "store" ? "КУХНЯ" : "СКЛАД",
        eventShortName: department === "store" ? "КУХНЯ" : "СКЛАД",
        isDeleted: false,
        isStore: department === "store",
        isMontage: department === "montage",
        eventStatus: "ЗАВЪРШЕНО",
        isLocked: false,
    } as RREventType);

    for (const item of calendarItems) {
        const eventDayStart = dateToString(item.eventDayStart)!;
        if(!item.eventDayEnd && item.dayType === "HOLIDAY") {
            item.eventDayEnd = new Date(item.eventDayStart);
            item.eventDayEnd.setHours(item.eventDayEnd.getHours() + 8);
        }
        if (item.eventDayEnd) {
            // if(item.eventId === "QogXBbLMhDsUmm4NQkQr") {
            //     debugger;
            // }
            const employee = employees.find(e => e?.id === item.employeeId);
            const eventDayEnd = dateWithZeroTime(item.eventDayEnd)!;
            eventDayEnd.setDate(eventDayEnd.getDate() + 1);

            if (employee) {
                const holidayIdx = holidayDates.includes(eventDayStart) ? 2 : 1;
                let supervisor_pph = (employee.supervisor_pph || 0) / 60;
                let pph = (employee.pph || 0) / 60;
                let supervisor_pph_orig = (employee.supervisor_pph || 0);
                let pph_orig = (employee.pph || 0);
                for(let i = employee.empl_pph.length-1; i >= 0; i--) {
                    const pphItem = employee.empl_pph[i];

                    if(pphItem.isApproved && getEmployeeRateEffectiveFrom(pphItem) < eventDayEnd) {
                        supervisor_pph = (pphItem.supervisor_pph || 0) / 60;
                        pph = (pphItem.pph || 0) / 60;
                        break;
                    }
                }

                let event = allEvents.find(e => e?.id === item.eventId);
                if(!event && item.dayType === "HOLIDAY") {
                    event = {
                        eventNumber: "HOLIDAY",
                        eventDayStart: item.eventDayStart,
                        eventDayEnd: item.eventDayEnd,
                        id: "HOLIDAY",
                        clientName: "HOLIDAY",
                        clientEmail: "",
                        eventCategory: "MONTAGE",
                        isCatering: false,
                        eventName: "HOLIDAY",
                        eventShortName: "HOLIDAY",
                        isDeleted: false,
                        isStore: false,
                        isMontage: false,
                        eventStatus: "ЗАВЪРШЕНО",
                        isLocked: false,
                    } as RREventType;
                }
                if (!event && item.eventId === "WAREHOUSE") {
                    event = createWarehouseEvent();
                }
                if (!event && item.eventId) {
                    // Keep the entry in report even if event document is missing/deleted.
                    event = {
                        eventNumber: item.eventId,
                        eventDayStart: item.eventDayStart,
                        eventDayEnd: item.eventDayEnd,
                        id: item.eventId,
                        clientName: "ЛИПСВАЩО СЪБИТИЕ",
                        clientEmail: "",
                        eventCategory: "MONTAGE",
                        isCatering: department === "catering",
                        eventName: "ЛИПСВАЩО СЪБИТИЕ",
                        eventShortName: "ЛИПСВАЩО СЪБИТИЕ",
                        isDeleted: false,
                        isStore: department === "store",
                        isMontage: department === "montage",
                        eventStatus: "ЗАВЪРШЕНО",
                        isLocked: false,
                    } as RREventType;
                }

                if (event) {
                    const emplItem = {
                        date: eventDayStart,
                        employee: {...employee, pph: pph_orig, supervisor_pph: supervisor_pph_orig},
                        price: 0,
                        minutes: 0,
                        minutesAsHoliday: 0,
                        minutesAsSupervisor: 0,
                        description: item.description,
                    } as RREmployeeReportItemType;

                    const minutes = timeDiffInMinutes(item.eventDayStart, item.eventDayEnd, item.restTime);
                    if(item.dayType === "HOLIDAY") {
                        emplItem.minutes += minutes;
                        emplItem.price += minutes * pph;
                    } else {
                        if (item.isSupervisor && item.dayType === "WORKDAY") {
                            emplItem.minutesAsSupervisor += minutes * holidayIdx;
                            emplItem.price += minutes * holidayIdx * supervisor_pph;
                        } else {
                            emplItem.minutes += minutes * holidayIdx;
                            emplItem.price += minutes * holidayIdx * pph;
                        }
                        emplItem.price += item.bonus || 0;
                    }

                    let emplReportItem = report.report.employees.find(e => e?.employee.id === item.employeeId && e?.date === eventDayStart);
                    if (!emplReportItem) {
                        emplReportItem = {...emplItem} as RREmployeeReportItemType;
                        report.report.employees.push(emplReportItem);
                    } else {
                        emplReportItem.minutes += emplItem.minutes;
                        emplReportItem.price += emplItem.price;
                        emplReportItem.minutesAsSupervisor += emplItem.minutesAsSupervisor;
                        emplReportItem.minutesAsHoliday += emplItem.minutesAsHoliday;
                    }

                    let eventReportItem = report.report.events.find(e => e?.event.eventNumber === event!.eventNumber && e?.date === eventDayStart);
                    if (!eventReportItem) {
                        eventReportItem = {
                            date: eventDayStart,
                            event: event,
                            employees: [],
                            minutesAsHoliday: 0,
                            minutesAsSupervisor: 0,
                            minutes: 0,
                            price: 0,
                            description: item.description,
                        } as RREventReportReportItemType;
                        report.report.events.push(eventReportItem);
                    }
                    if(item.dayType === "HOLIDAY") {
                        eventReportItem.minutes += minutes;
                        eventReportItem.price += minutes * pph;
                    } else {
                        if (item.isSupervisor && item.dayType === "WORKDAY") {
                            eventReportItem.minutesAsSupervisor += minutes * holidayIdx;
                            eventReportItem.price += minutes * holidayIdx * supervisor_pph;
                        } else {
                            eventReportItem.minutes += minutes * holidayIdx;
                            eventReportItem.price += minutes * holidayIdx * pph;
                        }

                        eventReportItem.price += item.bonus || 0;
                    }

                    eventReportItem.employees.push(emplItem);
                }
            }
        }
    }

    return report;
}
